/**
 * ============================================================================
 * Dynamic Shipping Calculator - WEIGHT-BASED VERSION
 * ============================================================================
 *
 * FEATURES:
 * - Weight-based tier selection (0-50g vs 100g+)
 * - Matches Shopify Admin shipping zones exactly
 * - Event-driven updates (no redundant API calls)
 * - Fast performance via Liquid-embedded rates
 *
 * WEIGHT TIERS:
 * - Light: 0-50g (1 mitt) - Higher rates
 * - Heavy: 100g+ (2+ mitts) - Lower rates + FREE standard shipping
 *
 * BENEFITS:
 * - 100% accurate to Shopify Admin settings
 * - No API calls needed
 * - Instant rate display
 * - Auto-syncs with shipping-rates-config.liquid
 * ============================================================================
 */
(function() {
  'use strict';

  /* State Management */
  const state = {
    currentCountry: null,
    lastShippingText: null,
    lastTotalText: null,
    ratesCache: new Map(), // In-memory cache for session
  };

  /* Cache Configuration */
  const CACHE_KEY_PREFIX = 'folia-shipping-rates-';
  const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

  /* Weight Threshold - Determines which tier to use */
  const WEIGHT_THRESHOLD = 100; // 100g = 2 mitts at 50g each

  /* Fallback Configuration - Weight-based rates (matches shipping-rates-config.liquid) */
  const FALLBACK_RATES = {
    'AU': {
      light: { standard: 500, express: 1000 },
      heavy: { standard: 0, express: 500 }
    },
    'GB': {
      light: { standard: 500, express: 1000 },
      heavy: { standard: 0, express: 500 }
    },
    'AE': {
      light: { standard: 3000, express: 6000 },
      heavy: { standard: 0, express: 3000 }
    },
    // Europe & other countries: €10/€20 light, FREE/€10 heavy
    'DEFAULT': {
      light: { standard: 1000, express: 2000 },
      heavy: { standard: 0, express: 1000 }
    }
  };

  const FREE_SHIPPING_THRESHOLD = 2;

  /* Country Management */
  function getCurrentCountry() {
    if (state.currentCountry) return state.currentCountry;

    const savedCountry = localStorage.getItem('shopify_country_code');
    if (savedCountry) {
      state.currentCountry = savedCountry;
      return savedCountry;
    }

    if (window.Shopify && window.Shopify.country) {
      state.currentCountry = window.Shopify.country;
      return window.Shopify.country;
    }

    state.currentCountry = 'AU';
    return 'AU';
  }

  function getCountryName(countryCode) {
    if (window.ThemeGlobals && window.ThemeGlobals.getCountryName) {
      return window.ThemeGlobals.getCountryName(countryCode);
    }

    const countryNames = {
      'AU': 'Australia', 'CA': 'Canada', 'US': 'United States',
      'GB': 'UK', 'NZ': 'New Zealand', 'FR': 'France',
      'DE': 'Germany', 'IT': 'Italy', 'ES': 'Spain',
      'NL': 'Netherlands', 'CH': 'Switzerland', 'AE': 'UAE'
    };

    return countryNames[countryCode] || countryCode;
  }

  /* Cache Management */
  function getCachedRates(countryCode) {
    // Check in-memory cache first
    if (state.ratesCache.has(countryCode)) {
      return state.ratesCache.get(countryCode);
    }

    // Check localStorage
    const cacheKey = CACHE_KEY_PREFIX + countryCode;
    const cached = localStorage.getItem(cacheKey);

    if (cached) {
      try {
        const data = JSON.parse(cached);
        const age = Date.now() - data.timestamp;

        if (age < CACHE_DURATION) {
          // Cache hit - store in memory and return
          state.ratesCache.set(countryCode, data.rates);
          return data.rates;
        } else {
          // Cache expired
          localStorage.removeItem(cacheKey);
        }
      } catch (e) {
        localStorage.removeItem(cacheKey);
      }
    }

    return null;
  }

  function setCachedRates(countryCode, rates) {
    const cacheKey = CACHE_KEY_PREFIX + countryCode;
    const data = {
      rates: rates,
      timestamp: Date.now()
    };

    // Store in both memory and localStorage
    state.ratesCache.set(countryCode, rates);

    try {
      localStorage.setItem(cacheKey, JSON.stringify(data));
    } catch (e) {
      // localStorage might be full or disabled
      console.warn('Failed to cache shipping rates:', e);
    }
  }

  /* Calculate total cart weight in grams */
  function calculateCartWeight(cart) {
    if (!cart || !cart.items) return 0;

    return cart.items.reduce((totalWeight, item) => {
      const itemWeight = item.grams || 0;
      const itemQuantity = item.quantity || 0;
      return totalWeight + (itemWeight * itemQuantity);
    }, 0);
  }

  /* Fetch Real Shipping Rates from Shopify Liquid Variables - Weight-based */
  function fetchShopifyRatesFromLiquid(countryCode, cart) {
    // Check if shipping rates were embedded by Liquid
    if (window.ThemeGlobals && window.ThemeGlobals.shippingRates) {
      const countryRates = window.ThemeGlobals.shippingRates[countryCode];

      if (countryRates && countryRates.light && countryRates.heavy) {
        // Calculate cart weight to determine tier
        const cartWeight = calculateCartWeight(cart);
        const weightThreshold = window.ThemeGlobals.weightThreshold || WEIGHT_THRESHOLD;

        // Select appropriate tier based on weight
        const tier = cartWeight >= weightThreshold ? 'heavy' : 'light';
        const selectedRates = countryRates[tier];

        return {
          standard: selectedRates.standard,
          express: selectedRates.express,
          source: 'shopify-liquid',
          tier: tier,
          cartWeight: cartWeight
        };
      }
    }
    return null;
  }

  /* Fetch from metaobjects or settings (if configured) */
  async function fetchShopifyRates(countryCode, cart = null) {
    try {
      // Try Liquid-embedded rates (fastest, most reliable)
      const liquidRates = fetchShopifyRatesFromLiquid(countryCode, cart);
      if (liquidRates) {
        return liquidRates;
      }

      // Fallback if Liquid rates not available
      console.warn('[Shipping] No Liquid-embedded rates found - using fallback configuration');

      return null;
    } catch (error) {
      console.warn('[Shipping] Failed to fetch shipping rates:', error);
      return null;
    }
  }

  /* Get Shipping Rate - Weight-based tier selection */
  async function getShippingRate(countryCode, cart = null) {
    if (typeof countryCode !== 'string') {
      countryCode = 'AU';
    }

    // 1. Try to fetch real rates from Liquid-embedded config (primary source)
    if (cart && cart.items) {
      const shopifyRates = await fetchShopifyRates(countryCode, cart);
      if (shopifyRates && typeof shopifyRates.standard !== 'undefined') {
        return shopifyRates;
      }
    }

    // 2. Fallback to hardcoded weight-based rates
    const cartWeight = calculateCartWeight(cart);
    const tier = cartWeight >= WEIGHT_THRESHOLD ? 'heavy' : 'light';

    // Get country-specific rates or use default
    const countryRates = FALLBACK_RATES[countryCode] || FALLBACK_RATES['DEFAULT'];
    const tierRates = countryRates[tier];

    return {
      standard: tierRates.standard,
      express: tierRates.express,
      source: 'fallback',
      tier: tier,
      cartWeight: cartWeight
    };
  }

  /* Currency Management */
  function getCurrencyForCountry(countryCode) {
    if (window.ThemeGlobals && window.ThemeGlobals.getCountryCurrency) {
      return window.ThemeGlobals.getCountryCurrency(countryCode) || 'USD';
    }

    const currencyMap = {
      'AU': 'AUD', 'CA': 'CAD', 'NZ': 'NZD', 'SG': 'SGD',
      'US': 'USD', 'GB': 'GBP', 'CH': 'CHF', 'AE': 'AED'
    };

    const eurCountries = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE',
                         'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV',
                         'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
                         'SI', 'ES', 'SE'];

    if (eurCountries.includes(countryCode)) return 'EUR';
    return currencyMap[countryCode] || 'USD';
  }

  function formatMoneyWithoutCode(cents, currencyCode) {
    const amount = (cents / 100).toFixed(2);

    const symbols = {
      'AUD': '$', 'CAD': '$', 'NZD': '$', 'SGD': '$', 'USD': '$',
      'EUR': '€', 'GBP': '£', 'CHF': 'CHF ', 'AED': 'AED '
    };

    const symbol = symbols[currencyCode] || '';
    return `${symbol}${amount}`;
  }

  function formatMoneyWithCode(cents, currencyCode) {
    const base = formatMoneyWithoutCode(cents, currencyCode);

    if (currencyCode === 'CHF' || currencyCode === 'AED') {
      return base;
    }

    return `${base} ${currencyCode}`;
  }

  /* UI Updates */
  function smoothUpdate(element, newValue) {
    if (!element || newValue.includes('Calculating') || element.textContent === newValue) return;

    element.classList.add('value-updating');

    setTimeout(() => {
      element.textContent = newValue;
      element.classList.remove('value-updating');
    }, 150);
  }

  /**
   * CORE FUNCTION - Uses cart data from event instead of fetching
   */
  async function updateShippingAndTotal(cart) {
    try {
      const shippingLabelEl = document.querySelector('[data-shipping-label]');
      const shippingCostEl = document.querySelector('[data-shipping-cost]');
      const totalValueEl = document.querySelector('[data-cart-subtotal]');
      const discountValueEls = document.querySelectorAll('[data-discount-value]');

      if (!shippingCostEl) return;

      // Calculate item count (excluding gift cards)
      const nonGiftItems = cart.items.filter(item => !item.gift_card);
      const itemCount = nonGiftItems.reduce((sum, item) => sum + item.quantity, 0);

      // Get location and rate information
      const countryCode = getCurrentCountry();
      const countryName = getCountryName(countryCode);
      const shippingRate = await getShippingRate(countryCode, cart);
      const shippingCurrency = getCurrencyForCountry(countryCode);

      console.log('[Shipping] Using rates:', {
        country: countryCode,
        tier: shippingRate.tier || 'unknown',
        cartWeight: `${shippingRate.cartWeight || 0}g`,
        standard: `$${(shippingRate.standard / 100).toFixed(2)}`,
        express: `$${(shippingRate.express / 100).toFixed(2)}`,
        source: shippingRate.source
      });

      // Calculate shipping
      let shippingCost = 0;
      let shippingText = '';
      let shippingLabel = countryCode === 'AU' ?
        'Postage in Australia' : `Shipping to ${countryName}`;

      if (itemCount >= FREE_SHIPPING_THRESHOLD) {
        shippingText = 'FREE';
      } else if (itemCount > 0) {
        shippingCost = shippingRate.standard;
        shippingText = formatMoneyWithCode(shippingCost, shippingCurrency);
      } else {
        shippingText = '–';
      }

      state.lastShippingText = shippingText;

      // Calculate new total
      const currentTotal = cart.total_price;
      const newTotal = currentTotal + (shippingText === 'FREE' ? 0 : shippingCost);
      const displayCurrency = cart.currency || shippingCurrency;
      const formattedTotal = formatMoneyWithCode(newTotal, displayCurrency);

      // Update UI
      if (shippingLabelEl) {
        smoothUpdate(shippingLabelEl, shippingLabel);
      }

      smoothUpdate(shippingCostEl, shippingText);

      if (itemCount >= FREE_SHIPPING_THRESHOLD) {
        setTimeout(() => shippingCostEl.classList.add('free-shipping'), 200);
      } else {
        shippingCostEl.classList.remove('free-shipping');
      }

      // Update discounts
      if (discountValueEls.length > 0) {
        discountValueEls.forEach((el, index) => {
          const discount = cart.cart_level_discount_applications[index];
          if (discount) {
            const formattedDiscount = `-${formatMoneyWithCode(discount.total_allocated_amount, displayCurrency)}`;
            smoothUpdate(el, formattedDiscount);
            el.parentElement.style.display = 'flex';
          } else {
            el.textContent = '';
            el.parentElement.style.display = 'none';
          }
        });
      }

      // Update total
      if (totalValueEl) {
        smoothUpdate(totalValueEl, formattedTotal);
        totalValueEl.setAttribute('value', (newTotal / 100).toFixed(2));
        state.lastTotalText = formattedTotal;

        updateBNPLDisplay(newTotal, displayCurrency);
      }

    } catch (error) {
      console.error('Error updating shipping:', error);
    }
  }

  function updateBNPLDisplay(totalCents, currencyCode, showCurrencyCodeSetting) {
    const bnplContainer = document.querySelector('.bnpl-cart-snippet-container');
    const bnplPriceEl = document.querySelector('.bnpl-cart-price-value');

    if (bnplContainer && bnplPriceEl) {
      const installmentsCount = parseInt(bnplContainer.dataset.installmentsCount || '4', 10) || 4;
      const showCurrencyCode = showCurrencyCodeSetting ?? (bnplContainer.dataset.showCurrencyCode === 'true');

      bnplContainer.setAttribute('data-cart-total-cents', totalCents);

      const baseInstallment = Math.floor(totalCents / installmentsCount);
      const remainder = totalCents % installmentsCount;
      const installmentAmount = remainder > 0 ? baseInstallment + 1 : baseInstallment;

      const formattedInstallment = showCurrencyCode
        ? formatMoneyWithCode(installmentAmount, currencyCode)
        : formatMoneyWithoutCode(installmentAmount, currencyCode);

      smoothUpdate(bnplPriceEl, formattedInstallment);
    }
  }

  /**
   * EVENT HANDLER - Listens to cart:update events
   */
  function handleCartUpdate(event) {
    const cart = event.detail.resource;

    // If cart data is available in the event, use it
    if (cart && cart.items) {
      updateShippingAndTotal(cart);
    } else {
      // Fallback: fetch cart data if not in event
      fetchCartAndUpdate();
    }
  }

  /**
   * FALLBACK - Only used for initial page load
   */
  async function fetchCartAndUpdate() {
    try {
      const response = await fetch('/cart.js', {
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cart = await response.json();
      updateShippingAndTotal(cart);

    } catch (error) {
      console.error('Error fetching cart:', error);

      const shippingCostEl = document.querySelector('[data-shipping-cost]');
      if (shippingCostEl) {
        smoothUpdate(shippingCostEl, '–');
      }
    }
  }

  /* Debug Helper - Shows current shipping configuration */
  function logShippingConfig() {
    console.group('📦 Folia Weight-Based Shipping Rates');

    if (window.ThemeGlobals && window.ThemeGlobals.shippingRates) {
      console.log('✅ Liquid-embedded rates loaded');
      console.log('Weight threshold:', window.ThemeGlobals.weightThreshold || WEIGHT_THRESHOLD, 'grams');

      const countries = Object.keys(window.ThemeGlobals.shippingRates);
      console.log('Countries configured:', countries.length);

      // Show example rates for current country
      const currentCountry = getCurrentCountry();
      const rates = window.ThemeGlobals.shippingRates[currentCountry];

      if (rates) {
        console.log(`\nRates for ${currentCountry}:`);
        console.log('  Light tier (0-50g):');
        console.log('    Standard:', `$${(rates.light.standard / 100).toFixed(2)}`);
        console.log('    Express:', `$${(rates.light.express / 100).toFixed(2)}`);
        console.log('  Heavy tier (100g+):');
        console.log('    Standard:', rates.heavy.standard === 0 ? 'FREE' : `$${(rates.heavy.standard / 100).toFixed(2)}`);
        console.log('    Express:', `$${(rates.heavy.express / 100).toFixed(2)}`);
      }
    } else {
      console.warn('⚠️ No Liquid-embedded rates - using fallback configuration');
    }

    console.groupEnd();
  }

  /* Initialization */
  async function init() {
    const currentCountry = getCurrentCountry();

    console.log('[Shipping Calculator] Weight-based tier system initialized');
    console.log('[Shipping Calculator] Current country:', currentCountry);
    console.log('[Shipping Calculator] Weight threshold:', WEIGHT_THRESHOLD, 'grams');

    // Show configuration on load
    logShippingConfig();

    // Initial cart fetch
    fetchCartAndUpdate();

    // Listen to cart:update events (PRIMARY DATA SOURCE)
    document.addEventListener('cart:update', handleCartUpdate);

    // Also listen to cart:add events
    document.addEventListener('cart:add', handleCartUpdate);

    // Country selector changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-country-selector]')) {
        const newCountry = e.target.value;
        state.currentCountry = newCountry;
        localStorage.setItem('shopify_country_code', newCountry);

        console.log('[Shipping] Country changed to:', newCountry);

        // Update shipping display
        fetchCartAndUpdate();
      }
    });

    // Expose debug helper to window for manual inspection
    window.foliaDev = window.foliaDev || {};
    window.foliaDev.showShippingConfig = logShippingConfig;

    console.log('💡 Tip: Run window.foliaDev.showShippingConfig() to view shipping configuration');

    return function cleanup() {
      document.removeEventListener('cart:update', handleCartUpdate);
      document.removeEventListener('cart:add', handleCartUpdate);
    };
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      const cleanup = init();
      window.addEventListener('unload', cleanup);
    });
  } else {
    const cleanup = init();
    window.addEventListener('unload', cleanup);
  }
})();
